import { FormEvent, useEffect, useId, useMemo, useState } from "react";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { EarTag, FormattedTags, useFormatEarTag } from "../../components/tags/EarTag";
import { Field } from '../../components/forms/Field';
import { FormModal } from '../../components/modals/FormModal';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import { formatDate, formatMoney, todayIso } from "../../lib/format";
import { inPeriod, toAmount, type FinancePeriod } from "../../lib/financePeriod";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { useModalHistoryEntry } from "../../lib/modalHistory";
import { mutateOrQueue, offlineQueuedInfo } from "../../lib/offline/helpers";
import { drainOutbox } from "../../lib/offline/offlineSync";
import {
  requireOfflineAdminPassword,
  storeAdminPasswordVerifier,
} from "../../lib/offline/offlineAdminPassword";
import { appendToReplicaList, loadFromReplica, patchReplica, saveToReplica } from "../../lib/offline/replica";
import { tagMatchesFilter } from "../../lib/tagVerificationInput";
import type { FinanceCategory, FinanceEntryType, FinanceTransaction } from "../../types";

type AssignMode = "none" | "sheep" | "herd";
type ListFilter = "all" | "expense" | "income";

const ASSIGN_TAGS_PREVIEW = 3;

const EMPTY = {
  type: "expense" as FinanceEntryType,
  amount: "",
  date: todayIso(),
  category: "feed",
  subcategory: "",
  note: "",
  password: "",
  has_photo: false,
};

function categoryLabel(categories: FinanceCategory[], id: string): string {
  return categories.find((item) => item.id === id)?.label || id;
}

function AssignTagsCell({ tags }: { tags: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const extra = tags.length - ASSIGN_TAGS_PREVIEW;
  const visible = expanded ? tags : tags.slice(0, ASSIGN_TAGS_PREVIEW);
  return (
    <div className="tbl__assign-wrap">
      <ul className="tbl__assign-list">
        {visible.map((tag) => (
          <li key={tag}>
            <EarTag value={tag} />
          </li>
        ))}
      </ul>
      {extra > 0 ? (
        <button
          type="button"
          className="tbl__assign-more"
          data-testid="expense-assign-more"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? "Zwiń" : `+${extra} więcej`}
        </button>
      ) : null}
    </div>
  );
}

export function ExpensesPage() {
  const formatTag = useFormatEarTag();
  const fieldNs = useId().replace(/:/g, "");
  const [categories, setCategories] = useState<FinanceCategory[]>([]);
  const [items, setItems] = useState<FinanceTransaction[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [assignMode, setAssignMode] = useState<AssignMode>("none");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagQuery, setTagQuery] = useState("");
  const [period, setPeriod] = useState<FinancePeriod>("month");
  const [structureType, setStructureType] = useState<FinanceEntryType>("expense");
  const [listFilter, setListFilter] = useState<ListFilter>("all");
  const [photoName, setPhotoName] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catLabel, setCatLabel] = useState("");
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subLabel, setSubLabel] = useState("");

  const matchingCategories = useMemo(
    () => categories.filter((category) => category.type === form.type),
    [categories, form.type],
  );
  const selectedCategory = matchingCategories.find((category) => category.id === form.category);

  const periodItems = useMemo(
    () => items.filter((item) => inPeriod(item.date, period)),
    [items, period],
  );

  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    for (const item of periodItems) {
      const amount = toAmount(item.amount);
      if (item.type === "income") {
        income += amount;
      } else {
        expense += amount;
      }
    }
    return { income, expense, net: income - expense };
  }, [periodItems]);

  const structureBars = useMemo(() => {
    const cats = categories.filter((category) => category.type === structureType);
    const sums = new Map<string, number>();
    for (const cat of cats) {
      sums.set(cat.id, 0);
    }
    for (const item of periodItems) {
      if (item.type !== structureType) {
        continue;
      }
      sums.set(item.category, (sums.get(item.category) || 0) + toAmount(item.amount));
    }
    const total = [...sums.values()].reduce((sum, value) => sum + value, 0);
    return cats
      .map((cat) => {
        const amount = sums.get(cat.id) || 0;
        return {
          id: cat.id,
          label: cat.label,
          amount,
          pct: total > 0 ? Math.round((amount / total) * 100) : 0,
        };
      })
      .filter((row) => row.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [categories, periodItems, structureType]);

  const filteredList = useMemo(() => {
    if (listFilter === "all") {
      return items;
    }
    return items.filter((item) => item.type === listFilter);
  }, [items, listFilter]);

  const filteredAssignTags = useMemo(() => {
    const q = tagQuery.trim();
    if (!q) {
      return tags;
    }
    return tags.filter((tag) => tagMatchesFilter(tag, q));
  }, [tags, tagQuery]);

  async function load() {
    setBusy(true);
    
    // Load with replica fallback
    let catData: FinanceCategory[] | null = null;
    let txData: FinanceTransaction[] | null = null;
    
    if (navigator.onLine) {
      const [catRes, txRes, tagsRes] = await Promise.all([
        api.GET("/api/finance/categories"),
        api.GET("/api/finance/transactions"),
        api.GET("/api/tags"),
      ]);
      
      if (!catRes.error && catRes.data) {
        catData = catRes.data as FinanceCategory[];
        await saveToReplica("finance:categories", catData);
      }
      
      if (!txRes.error && txRes.data) {
        txData = txRes.data as FinanceTransaction[];
        await saveToReplica("finance:transactions", txData);
      }
      
      if (!tagsRes.error && tagsRes.data && typeof tagsRes.data === "object") {
        const payload = tagsRes.data as { tags?: string[] };
        setTags(payload.tags ?? []);
      }
    }
    
    // Fallback to replica if online failed or offline
    if (!catData) {
      catData = await loadFromReplica<FinanceCategory[]>("finance:categories");
    }
    if (!txData) {
      txData = await loadFromReplica<FinanceTransaction[]>("finance:transactions");
    }
    
    setBusy(false);
    
    if (!catData || !txData) {
      setError("Nie udało się wczytać danych.");
      return;
    }
    
    setError(null);
    setCategories(catData);
    setItems(txData);
    setForm((current) => {
      const match = catData!.find(
        (category) => category.type === current.type && category.id === current.category,
      );
      if (match) {
        return current;
      }
      const first = catData!.find((category) => category.type === current.type);
      return first ? { ...current, category: first.id, subcategory: "" } : current;
    });
  }

  useEffect(() => {
    void (async () => {
      const synced = await drainOutbox();
      if (synced.synced > 0) {
        setInfo(`Zsynchronizowano ${synced.synced} wpisów offline.`);
      }
      await load();
    })();
  }, []);

  useModalHistoryEntry(deleteId != null, () => {
    setDeleteId(null);
    setDeletePassword("");
  });
  useModalHistoryEntry(catModalOpen, () => setCatModalOpen(false));
  useModalHistoryEntry(subModalOpen, () => setSubModalOpen(false));

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setAssignMode("none");
    setSelectedTags([]);
    setTagQuery("");
    setPhotoName(null);
    setForm({ ...EMPTY, date: todayIso() });
  }

  function openCreateForm(type: FinanceEntryType) {
    setEditingId(null);
    setAssignMode("none");
    setSelectedTags([]);
    setTagQuery("");
    setPhotoName(null);
    const first = categories.find((category) => category.type === type);
    setForm({
      ...EMPTY,
      type,
      category: first?.id ?? (type === "expense" ? "feed" : ""),
      date: todayIso(),
    });
    setFormOpen(true);
    setInfo(null);
    setError(null);
  }

  function buildAssignPayload() {
    if (assignMode === "herd") {
      return { assign: null, assign_tags: [] as string[], assign_herd: true };
    }
    if (assignMode === "sheep") {
      return { assign: null, assign_tags: selectedTags.slice(), assign_herd: false };
    }
    return { assign: null, assign_tags: [] as string[], assign_herd: false };
  }

  function startEdit(item: FinanceTransaction) {
    if (!navigator.onLine) {
      setError("Edycja wymaga połączenia z internetem.");
      return;
    }
    setEditingId(item.id);
    setForm({
      type: item.type,
      amount: String(item.amount),
      date: item.date,
      category: item.category,
      subcategory: item.subcategory || "",
      note: item.note || "",
      password: "",
      has_photo: item.has_photo,
    });
    setPhotoName(item.has_photo ? "Zdjęcie zapisane" : null);
    if (item.assign_herd) {
      setAssignMode("herd");
      setSelectedTags([]);
    } else if (item.assign_tags?.length) {
      setAssignMode("sheep");
      setSelectedTags(item.assign_tags);
    } else {
      setAssignMode("none");
      setSelectedTags([]);
    }
    setFormOpen(true);
    setInfo(null);
    setError(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const amount = Number(form.amount.replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Podaj kwotę większą od zera.");
      return;
    }
    if (assignMode === "sheep" && selectedTags.length === 0) {
      setError("Wybierz co najmniej jedną owcę albo zmień tryb przypisania.");
      return;
    }
    if (assignMode === "herd" && tags.length === 0) {
      setError("Brak aktywnych zwierząt — nie można przypisać do całego stada.");
      return;
    }
    if (editingId != null && !form.password) {
      setError("Podaj hasło administratora.");
      return;
    }
    const assign = buildAssignPayload();
    const basePayload = {
      type: form.type,
      amount: amount.toFixed(2),
      date: form.date,
      category: form.category,
      subcategory: form.subcategory || null,
      note: form.note.trim() || null,
      ...assign,
      has_photo: form.has_photo,
    };

    setBusy(true);
    setInfo(null);

    if (editingId != null) {
      if (!navigator.onLine) {
        setBusy(false);
        setError("Edycja wymaga połączenia z internetem.");
        return;
      }
      const updated = await api.PATCH("/api/finance/transactions/{transaction_id}", {
        params: { path: { transaction_id: editingId } },
        body: { ...basePayload, password: form.password },
      });
      setBusy(false);
      if (updated.error) {
        setError(apiError(updated.error, "Nie udało się zapisać zmian."));
        return;
      }
      setError(null);
      setInfo("Zapisano zmiany.");
      closeForm();
      await load();
      return;
    }

    const clientId = crypto.randomUUID();
    const payload: Record<string, unknown> = { ...basePayload, client_id: clientId };

    // Dodaj hasło jeśli podane (dla tworzenia z hasłem admina)
    if (form.password) {
      payload.password = form.password;
    }

    if (payload.password) {
      const verifyError = await requireOfflineAdminPassword(payload.password as string);
      if (verifyError) {
        setBusy(false);
        setError(verifyError);
        return;
      }
    }

    const result = await mutateOrQueue("POST", "/api/finance/transactions", payload, clientId);
    setBusy(false);

    if (!result.success) {
      setError(apiError(result.error, "Nie udało się dodać transakcji."));
      return;
    }

    if (result.online) {
      // Sukces online - zapisz weryfikator hasła admina jeśli podane
      if (payload.password) {
        await storeAdminPasswordVerifier(payload.password as string);
      }
      setError(null);
      closeForm();
      await load();
    } else {
      const assign = buildAssignPayload();
      const optimistic: FinanceTransaction = {
        id: -Date.now(),
        type: form.type,
        amount,
        date: form.date,
        category: form.category,
        subcategory: form.subcategory || null,
        note: form.note.trim() || null,
        assign: null,
        assign_herd: assign.assign_herd,
        assign_tags: assign.assign_tags,
        assign_count: assign.assign_herd ? tags.length : assign.assign_tags.length,
        amount_per_unit:
          assign.assign_herd && tags.length > 0
            ? amount / tags.length
            : assign.assign_tags.length > 0
              ? amount / assign.assign_tags.length
              : null,
        has_photo: form.has_photo,
        created_at: new Date().toISOString(),
      };
      await appendToReplicaList("finance:transactions", optimistic);
      setItems((current) => [optimistic, ...current]);
      setError(null);
      setInfo(offlineQueuedInfo(result.needsPassword));
      closeForm();
    }
  }

  async function onDelete(event: FormEvent) {
    event.preventDefault();
    if (deleteId == null) {
      return;
    }
    if (!deletePassword) {
      setError("Podaj hasło administratora.");
      return;
    }

    const verifyError = await requireOfflineAdminPassword(deletePassword);
    if (verifyError) {
      setError(verifyError);
      return;
    }

    setBusy(true);
    const result = await mutateOrQueue(
      "DELETE",
      `/api/finance/transactions/${deleteId}`,
      { password: deletePassword, operation_id: crypto.randomUUID() },
    );
    setBusy(false);

    if (!result.success) {
      setError(apiError(result.error, "Nie udało się usunąć transakcji."));
      return;
    }

    if (result.online && deletePassword) {
      await storeAdminPasswordVerifier(deletePassword);
    }

    setDeleteId(null);
    setDeletePassword("");
    setError(null);
    setInfo(result.online ? "Usunięto transakcję." : offlineQueuedInfo(result.needsPassword));
    if (editingId === deleteId) {
      closeForm();
    }
    if (result.online) {
      await load();
    } else {
      const removedId = deleteId;
      await patchReplica<FinanceTransaction[]>("finance:transactions", (current) =>
        (Array.isArray(current) ? current : []).filter((item) => item.id !== removedId),
      );
      setItems((current) => current.filter((item) => item.id !== removedId));
    }
  }

  async function createCategory(event: FormEvent) {
    event.preventDefault();
    const label = catLabel.trim();
    if (!label) {
      setError("Podaj nazwę kategorii.");
      return;
    }
    setBusy(true);
    const created = await api.POST("/api/finance/categories", {
      body: { type: form.type, label },
    });
    setBusy(false);
    if (created.error) {
      setError(apiError(created.error, "Nie udało się dodać kategorii."));
      return;
    }
    const data = created.data as FinanceCategory;
    setCatModalOpen(false);
    setCatLabel("");
    await load();
    setForm((current) => ({ ...current, category: data.id, subcategory: "" }));
  }

  async function createSubcategory(event: FormEvent) {
    event.preventDefault();
    const label = subLabel.trim();
    if (!label || !form.category) {
      setError("Podaj nazwę podkategorii.");
      return;
    }
    setBusy(true);
    const created = await api.POST("/api/finance/categories/{category_id}/subcategories", {
      params: { path: { category_id: form.category } },
      body: { label },
    });
    setBusy(false);
    if (created.error) {
      setError(apiError(created.error, "Nie udało się dodać podkategorii."));
      return;
    }
    const data = created.data as { id: string };
    setSubModalOpen(false);
    setSubLabel("");
    await load();
    setForm((current) => ({ ...current, subcategory: data.id }));
  }

  const netClass =
    totals.net > 0
      ? "finance-card finance-card--positive"
      : totals.net < 0
        ? "finance-card finance-card--negative"
        : "finance-card finance-card--neutral";

  const perUnitHint =
    assignMode === "sheep" && selectedTags.length > 0 && form.amount
      ? toAmount(form.amount.replace(",", ".")) / selectedTags.length
      : assignMode === "herd" && tags.length > 0 && form.amount
        ? toAmount(form.amount.replace(",", ".")) / tags.length
        : null;

  return (
    <main className="page page--finance" data-testid="expenses-page">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">Finanse</p>
          <h1>Finanse hodowli</h1>
        </div>
        <div className="actions">
          <button
            type="button"
            className="btn"
            data-testid="expense-add-expense"
            onClick={() => openCreateForm("expense")}
          >
            Dodaj wydatek
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="expense-add-income"
            onClick={() => openCreateForm("income")}
          >
            Dodaj przychód
          </button>
        </div>
      </header>

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />

      <section className="panel">
        <div className="panel__header">
          <h2 className="panel__title">Podsumowanie</h2>
          <div className="actions" role="group" aria-label="Okres">
            {(
              [
                ["month", "Ten miesiąc"],
                ["quarter", "Kwartał"],
                ["year", "Rok"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={period === value ? "btn btn--sm" : "btn btn--ghost btn--sm"}
                data-testid={`finance-period-${value}`}
                onClick={() => setPeriod(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="finance-cards">
          <div className="finance-card">
            <p className="finance-card__label">Łączny przychód</p>
            <p className="finance-card__value" data-testid="finance-total-income">
              {formatMoney(totals.income)}
            </p>
          </div>
          <div className="finance-card">
            <p className="finance-card__label">Łączny wydatek</p>
            <p className="finance-card__value" data-testid="finance-total-expense">
              {formatMoney(totals.expense)}
            </p>
          </div>
          <div className={netClass}>
            <p className="finance-card__label">Czysty zysk / bilans</p>
            <p className="finance-card__value" data-testid="finance-net">
              {(totals.net > 0 ? "+" : totals.net < 0 ? "−" : "") +
                formatMoney(Math.abs(totals.net))}
            </p>
          </div>
        </div>

        <div className="panel__header" style={{ marginTop: "1rem" }}>
          <h3 className="panel__title">
            {structureType === "expense" ? "Struktura wydatków" : "Struktura przychodów"}
          </h3>
          <div className="actions">
            <button
              type="button"
              className={
                structureType === "expense" ? "btn btn--sm" : "btn btn--ghost btn--sm"
              }
              data-testid="finance-structure-expense"
              onClick={() => setStructureType("expense")}
            >
              Wydatki
            </button>
            <button
              type="button"
              className={
                structureType === "income" ? "btn btn--sm" : "btn btn--ghost btn--sm"
              }
              data-testid="finance-structure-income"
              onClick={() => setStructureType("income")}
            >
              Przychody
            </button>
          </div>
        </div>
        {structureBars.length === 0 ? (
          <p className="muted">
            Brak {structureType === "expense" ? "wydatków" : "przychodów"} w wybranym okresie.
          </p>
        ) : (
          <ul className="finance-bars" data-testid="finance-structure">
            {structureBars.map((row) => (
              <li key={row.id} className="finance-bars__row">
                <div className="finance-bars__meta">
                  <span>{row.label}</span>
                  <span>
                    {formatMoney(row.amount)} ({row.pct}%)
                  </span>
                </div>
                <div className="finance-bars__track">
                  <div className="finance-bars__fill" style={{ width: `${row.pct}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <FormModal
        open={formOpen}
        onClose={closeForm}
        title={
          editingId != null
            ? "Edytuj wpis"
            : form.type === "expense"
              ? "Dodaj wydatek"
              : "Dodaj przychód"
        }
        size="lg"
        testId="expense-form"
        onSubmit={onSubmit}
      >
        <div className="form__row">
          <Field label="Typ">
            <select
              className="input"
              data-testid="expense-type"
              value={form.type}
              onChange={(event) => {
                const nextType = event.target.value as FinanceEntryType;
                const first = categories.find((category) => category.type === nextType);
                setForm({
                  ...form,
                  type: nextType,
                  category: first?.id ?? "",
                  subcategory: "",
                });
              }}
            >
              <option value="expense">Wydatek</option>
              <option value="income">Przychód</option>
            </select>
          </Field>
          <Field label="Kwota (zł)">
            <input
              className="input"
              data-testid="expense-amount"
              inputMode="decimal"
              value={form.amount}
              onChange={(event) => setForm({ ...form, amount: event.target.value })}
              required
            />
          </Field>
          <Field label="Data">
            <input
              className="input"
              type="date"
              data-testid="expense-date"
              value={form.date}
              onChange={(event) => setForm({ ...form, date: event.target.value })}
              required
            />
          </Field>
        </div>
        <div className="form__row">
          <Field label="Kategoria">
            <div className="actions" style={{ width: "100%" }}>
              <select
                className="input"
                data-testid="expense-category"
                value={form.category}
                onChange={(event) =>
                  setForm({ ...form, category: event.target.value, subcategory: "" })
                }
                required
                style={{ flex: 1 }}
              >
                {matchingCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                data-testid="expense-add-category"
                onClick={() => {
                  setCatLabel("");
                  setCatModalOpen(true);
                }}
              >
                + Nowa
              </button>
            </div>
          </Field>
          <Field label="Podkategoria (opcjonalnie)">
            <div className="actions" style={{ width: "100%" }}>
              <select
                className="input"
                data-testid="expense-subcategory"
                value={form.subcategory}
                onChange={(event) => setForm({ ...form, subcategory: event.target.value })}
                style={{ flex: 1 }}
              >
                <option value="">—</option>
                {(selectedCategory?.subcategories ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                data-testid="expense-add-subcategory"
                disabled={!form.category}
                onClick={() => {
                  setSubLabel("");
                  setSubModalOpen(true);
                }}
              >
                + Nowa
              </button>
            </div>
          </Field>
        </div>
        <Field label="Notatka">
          <input
            className="input"
            data-testid="expense-note"
            value={form.note}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
            maxLength={200}
            placeholder={
              form.type === "income" ? "Np. sprzedaż jagniąt…" : "Np. siano z dostawy…"
            }
          />
        </Field>
        <Field label="Paragon / faktura">
          <label className={`photo-attach${form.has_photo ? " photo-attach--filled" : ""}`}>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="expense-photo"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setForm({ ...form, has_photo: Boolean(file) || form.has_photo });
                setPhotoName(file ? file.name : form.has_photo ? "Zdjęcie zapisane" : null);
              }}
            />
            <span>{photoName || (form.has_photo ? "Zdjęcie zapisane" : "Dodaj zdjęcie")}</span>
          </label>
        </Field>

        <fieldset className="finance-assign">
          <legend>Przypisz do owcy / grupy</legend>
          <div className="assign-mode" role="tablist" aria-label="Tryb przypisania">
            {(
              [
                ["none", "Brak"],
                ["sheep", "Wybrane"],
                ["herd", "Całe stado"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={assignMode === value}
                className={
                  assignMode === value
                    ? "assign-mode__btn assign-mode__btn--active"
                    : "assign-mode__btn"
                }
                data-testid={`expense-assign-${value}`}
                onClick={() => {
                  setAssignMode(value);
                  if (value !== "sheep") {
                    setSelectedTags([]);
                  }
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {assignMode === "sheep" ? (
            <div className="finance-assign__panel" data-testid="expense-assign-panel">
              <div className="finance-assign__search-row">
                <input
                  className="input"
                  type="search"
                  placeholder="Szukaj kolczyka…"
                  value={tagQuery}
                  onChange={(event) => setTagQuery(event.target.value)}
                  data-testid="expense-assign-search"
                  autoComplete="off"
                />
                <div className="finance-assign__quick">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      setSelectedTags((current) => {
                        const next = new Set(current);
                        for (const tag of filteredAssignTags) {
                          next.add(tag);
                        }
                        return [...next];
                      });
                    }}
                  >
                    Zaznacz widoczne
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setSelectedTags([])}
                  >
                    Wyczyść
                  </button>
                </div>
              </div>
              <div
                className="finance-assign__list"
                role="listbox"
                aria-label="Lista owiec"
                aria-multiselectable="true"
              >
                {filteredAssignTags.length === 0 ? (
                  <p className="finance-assign__empty">Brak kolczyków do wyboru.</p>
                ) : (
                  filteredAssignTags.map((tag) => {
                    const selected = selectedTags.includes(tag);
                    return (
                      <label
                        key={tag}
                        className={
                          selected
                            ? "finance-assign__option is-selected"
                            : "finance-assign__option"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(event) => {
                            setSelectedTags((current) =>
                              event.target.checked
                                ? [...current, tag]
                                : current.filter((item) => item !== tag),
                            );
                          }}
                        />
                        <span className="finance-assign__tag">
                          <EarTag value={tag} />
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              {selectedTags.length > 0 ? (
                <div className="finance-assign__chips" aria-live="polite">
                  <span className="finance-assign__chips-label">
                    Wybrane ({selectedTags.length}):
                  </span>
                  {selectedTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="finance-assign__chip"
                      title={`Usuń ${formatTag(tag)}`}
                      onClick={() =>
                        setSelectedTags((current) => current.filter((item) => item !== tag))
                      }
                    >
                      <EarTag value={tag} />
                      <span aria-hidden="true">×</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="finance-assign__hint">Zaznacz jedną lub więcej owiec z listy.</p>
              )}
              {perUnitHint != null ? (
                <p className="finance-assign__hint finance-assign__hint--strong">
                  {formatMoney(perUnitHint)} / szt
                </p>
              ) : null}
            </div>
          ) : null}
          {assignMode === "herd" ? (
            <p className="finance-assign__hint">
              Kwota zostanie rozłożona na całe aktywne stado
              {tags.length > 0 ? ` (${tags.length})` : ""}.
              {perUnitHint != null ? ` ${formatMoney(perUnitHint)} / szt` : ""}
            </p>
          ) : null}
        </fieldset>

        {editingId != null ? (
          <Field label="Hasło administratora">
            <input
              className="input"
              type="password"
              data-testid="expense-password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              required
              {...passwordFieldProps(`${fieldNs}-expense-password`)}
            />
          </Field>
        ) : null}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeForm}>
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy} data-testid="expense-save">
            {editingId ? "Zapisz zmiany" : "Zapisz"}
          </button>
        </div>
      </FormModal>

      <section className="panel panel--finance-tx">
        <div className="panel__header">
          <h2 className="panel__title">Ostatnie transakcje</h2>
          <div className="actions" role="group" aria-label="Filtr typu">
            {(
              [
                ["all", "Wszystkie"],
                ["expense", "Wydatki"],
                ["income", "Przychody"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={listFilter === value ? "btn btn--sm" : "btn btn--ghost btn--sm"}
                data-testid={`expenses-filter-${value}`}
                onClick={() => setListFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="table-wrap table-wrap--scroll table-wrap--finance-fill">
          <ResponsiveTable className="tbl tbl--finance" data-testid="expenses-list">
            <thead>
              <tr>
                <th>Data</th>
                <th>Typ</th>
                <th>Kategoria</th>
                <th>Kwota</th>
                <th>Przypisanie</th>
                <th className="tbl__note">Notatka</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length === 0 ? (
                <tr className="tbl__empty-row">
                  <td colSpan={7} className="muted">
                    Brak transakcji.
                  </td>
                </tr>
              ) : (
                filteredList.map((item) => (
                  <tr
                    key={item.id}
                    className={
                      item.type === "income" ? "tbl__row--income" : "tbl__row--expense"
                    }
                  >
                    <td>{formatDate(item.date)}</td>
                    <td>
                      <span
                        className={
                          item.type === "income"
                            ? "finance-type finance-type--income"
                            : "finance-type finance-type--expense"
                        }
                      >
                        {item.type === "income" ? "Przychód" : "Wydatek"}
                      </span>
                    </td>
                    <td>
                      {categoryLabel(categories, item.category)}
                      {item.has_photo ? " 📎" : ""}
                    </td>
                    <td
                      className={
                        item.type === "income"
                          ? "finance-amount finance-amount--income"
                          : "finance-amount finance-amount--expense"
                      }
                    >
                      {item.type === "income" ? "+" : "−"}
                      {formatMoney(item.amount)}
                    </td>
                    <td className="tbl__assign">
                      {item.assign_herd ? (
                        item.assign || "Całe stado"
                      ) : item.assign_tags?.length ? (
                        <AssignTagsCell tags={item.assign_tags} />
                      ) : (
                        item.assign || "—"
                      )}
                    </td>
                    <td className="tbl__note">
                      {item.note ? <FormattedTags text={item.note} /> : "—"}
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          data-testid="expense-edit"
                          onClick={() => startEdit(item)}
                        >
                          Edytuj
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          data-testid="expense-delete"
                          onClick={() => {
                            if (!navigator.onLine) {
                              setError("Usuwanie wymaga połączenia z internetem.");
                              return;
                            }
                            setDeletePassword("");
                            setDeleteId(item.id);
                          }}
                        >
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </div>
      </section>

      {deleteId != null ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="expense-delete-modal"
        >
          <form className="modal" onSubmit={onDelete}>
            <h3 className="modal__title">Usunąć transakcję?</h3>
            <p className="modal__text">Tej operacji nie da się cofnąć.</p>
            <Field label="Hasło administratora">
              <input
                className="input"
                type="password"
                data-testid="expense-delete-password"
                value={deletePassword}
                onChange={(event) => setDeletePassword(event.target.value)}
                required
                {...passwordFieldProps(`${fieldNs}-expense-delete-password`)}
              />
            </Field>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setDeleteId(null);
                  setDeletePassword("");
                }}
              >
                Anuluj
              </button>
              <button
                type="submit"
                className="btn btn--danger"
                data-testid="expense-delete-confirm"
                disabled={busy}
              >
                Usuń
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {catModalOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="expense-category-modal"
        >
          <form className="modal" onSubmit={createCategory}>
            <h3 className="modal__title">Nowa kategoria</h3>
            <Field label="Nazwa">
              <input
                className="input"
                data-testid="expense-category-label"
                value={catLabel}
                onChange={(event) => setCatLabel(event.target.value)}
                required
              />
            </Field>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setCatModalOpen(false)}>
                Anuluj
              </button>
              <button type="submit" className="btn" disabled={busy}>
                Dodaj
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {subModalOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="expense-subcategory-modal"
        >
          <form className="modal" onSubmit={createSubcategory}>
            <h3 className="modal__title">Nowa podkategoria</h3>
            <Field label="Nazwa">
              <input
                className="input"
                data-testid="expense-subcategory-label"
                value={subLabel}
                onChange={(event) => setSubLabel(event.target.value)}
                required
              />
            </Field>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setSubModalOpen(false)}>
                Anuluj
              </button>
              <button type="submit" className="btn" disabled={busy}>
                Dodaj
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}
